import { ExperimentService } from '../experiments'
import { GeoLocationService } from '../geo-location'
import { SubscriptionProPlan } from '../subscription'

export type LocalizedExperimentIndiaVariantName = 'test_b' | 'test_c' | 'test_d' | 'control_a'

export interface LocalizedPriceEligibility {
  isEligible: boolean
  variantName: LocalizedExperimentIndiaVariantName | ''
}

export enum SpecialOfferName {
  LOCALIZED_PRICES_INDIA_TEST_A = 'localized_prices_india_test_a',
  LOCALIZED_PRICES_INDIA_TEST_B = 'localized_prices_india_test_b',
  LOCALIZED_PRICES_INDIA_TEST_C = 'localized_prices_india_test_c',
}

const STATES_TEST_A = [
  'maharashtra',
  'west bengal',
  'bihar',
  'uttarakhand',
  'jharkhand',
  'union territory of puducherry',
  'andaman and nicobar',
  'jammu and kashmir',
]

const STATES_TEST_B = [
  'tamil nadu',
  'telangana',
  'kerala',
  'rajasthan',
  'assam',
  'goa',
  'manipur',
  'tripura',
  'dadra and nagar haveli and daman and diu',
]

const STATES_TEST_C = [
  'national capital territory of delhi',
  'gujarat',
  'haryana',
  'madhya pradesh',
  'andhra pradesh',
  'chhattisgarh',
  'meghalaya',
  'nagaland',
]

const LOCALIZED_PRICES = {
  test_b: {
    monthly: 1000,
    quarterly: 2000,
    annual: 4700,
  },
  test_c: {
    monthly: 600,
    quarterly: 1200,
    annual: 3000,
  },
  test_d: {
    monthly: 250,
    quarterly: 500,
    annual: 1200,
  },
}

export const LOCALIZED_PRICES_INDIA_EXPERIMENT_NAME = 'localized_prices_india'
export const LOCALIZED_PRICES_INDIA_EXPERIMENT_ANON_NAME = 'localized_prices_india_anon'

export const VARIANTS_NAMES: Record<string, LocalizedExperimentIndiaVariantName> = {
  [SpecialOfferName.LOCALIZED_PRICES_INDIA_TEST_A]: 'test_b',
  [SpecialOfferName.LOCALIZED_PRICES_INDIA_TEST_B]: 'test_c',
  [SpecialOfferName.LOCALIZED_PRICES_INDIA_TEST_C]: 'test_d',
  Control: 'control_a',
}

export interface LocalizedPricesIndiaService {
  isEligibleForLocalizedPlansIndiaAnon(): LocalizedPriceEligibility
  mapPlansToLocalizedPrice(plan: SubscriptionProPlan, variantName: string): SubscriptionProPlan
}

export class LocalizedPricesIndiaServiceImpl implements LocalizedPricesIndiaService {
  private experimentsService: ExperimentService
  private geoLocationService: GeoLocationService

  constructor(experimentsService: ExperimentService, geoLocationService: GeoLocationService) {
    this.experimentsService = experimentsService
    this.geoLocationService = geoLocationService
  }

  isEligibleForLocalizedPlansIndiaAnon(): LocalizedPriceEligibility {
    if (!this.experimentsService.isLocalizedPlansIndiaAnonEnabled()) {
      return {
        isEligible: false,
        variantName: '',
      }
    }

    const subdivisionName = this.geoLocationService.subdivisionName

    if (!subdivisionName) {
      return {
        isEligible: false,
        variantName: '',
      }
    }

    const state = subdivisionName.toLowerCase()

    if (STATES_TEST_A.includes(state)) {
      return {
        isEligible: true,
        variantName: VARIANTS_NAMES[SpecialOfferName.LOCALIZED_PRICES_INDIA_TEST_A],
      }
    }

    if (STATES_TEST_B.includes(state)) {
      return {
        isEligible: true,
        variantName: VARIANTS_NAMES[SpecialOfferName.LOCALIZED_PRICES_INDIA_TEST_B],
      }
    }

    if (STATES_TEST_C.includes(state)) {
      return {
        isEligible: true,
        variantName: VARIANTS_NAMES[SpecialOfferName.LOCALIZED_PRICES_INDIA_TEST_C],
      }
    }

    // Default case: eligible with control variant
    return {
      isEligible: true,
      variantName: VARIANTS_NAMES.Control,
    }
  }

  mapPlansToLocalizedPrice(plan: SubscriptionProPlan, variantName: string): SubscriptionProPlan {
    const localizedPrices = LOCALIZED_PRICES[variantName as keyof typeof LOCALIZED_PRICES]

    if (!localizedPrices) {
      return plan
    }

    return {
      ...plan,
      monthlyPrice: localizedPrices.annual / 12,
      monthlyPricePerMonth: localizedPrices.monthly,
      currency: plan.currency,
    }
  }
}
